import { useParams } from 'react-router-dom';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    BarrierGateIdentificationTicket,
    OffstreetTransactionActions,
    PassageInfo,
} from './OperatorOffstreetTransactionTypes.ts';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { Localized, useLocalize } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import {
    BarrierGateIdentificationEnityValue,
    getNetPayedPriceString,
    NoReceiptForContractWithoutPayments,
    NoReceiptForRunningContract,
    OffstreetTransactionActionText,
    OffstreetTransactionOpen,
    OffstreetTransactionPassageText,
    OffstreetTransactionRefundBlocked,
    OffstreetTransactionTransit,
    OperatorOffstreetTransactionDetailRoutedModal,
} from './OperatorOffstreetTransactionShared.tsx';
import { type JSX, useEffect, useState } from 'react';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { Box, Stack, Tooltip } from '@mui/material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ErrorRounded, PendingRounded } from '@mui/icons-material';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { TicketState } from './OperatorOffstreetTransactionsRoute.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import {
    isInputAmountRefundable,
    OperatorRefund,
    parseChf,
    TransactionRefundBlockedExplanation,
    TransactionRefundInputValues,
    TransactionRefundPayload,
} from '../common/models/OperatorRefund.ts';
import {
    TransactionRefundInputFields,
    TransactionRefundResponseBanner,
} from '../common/components/TransactionRefund.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { ContractRecodeInfo } from './OperatorOffstreetTransactionContract.tsx';

enum TicketOffstreetTransactionType {
    TICKET = 'TICKET',
    TICKET_CONTRACT = 'TICKET_CONTRACT',
}

interface TicketOffstreetTransactionDetailBase {
    zoneName: string;
    entryInfo: PassageInfo;
    barrierGateIdentificationEntity: BarrierGateIdentificationTicket;
}

interface MonocardTicketOffstreetTransactionDetails extends TicketOffstreetTransactionDetailBase {
    ticketOffstreetTransactionType: TicketOffstreetTransactionType.TICKET;
    refundBlockedExplanation: TransactionRefundBlockedExplanation;
    ticketState: TicketState;
    unapprovedAt: string | null;
}

export interface PaystationPaymentData {
    netPaidAmountRappen: number;
    paidAt: string;
}

interface TicketContractOffstreetTransactionDetails extends TicketOffstreetTransactionDetailBase {
    ticketOffstreetTransactionType: TicketOffstreetTransactionType.TICKET_CONTRACT;
    contractRecodes: ContractRecodeInfo[];
    paystationPayments: PaystationPaymentData[];
    displayLpn: string | null;
    isCloudConnector: boolean;
    refundBlockedExplanation: TransactionRefundBlockedExplanation | null;
    contractExternalId: string;
    paymentChannel: PaymentChannel;
    transitsInfo: PassageInfo[] | null;
    exitInfo: PassageInfo;
    createdAt: string;
    duration: string;
    priceChf: number;
    refund: OperatorRefund | null;
    showGuestPermitGeneration: boolean;
}

type TicketOffstreetTransactionDetailTypes =
    | MonocardTicketOffstreetTransactionDetails
    | TicketContractOffstreetTransactionDetails;

export function OperatorOffstreetTransactionTicket() {
    const urlParams = useParams<{ ticketId: string }>();
    const ticketId = urlParams.ticketId ? urlParams.ticketId : null;
    const [offstreetTransactionsState, refetchOffstreetTransaction] =
        useServerFetch<
            TicketOffstreetTransactionDetailTypes,
            { ticketId: string },
            null
        >(
            context => ({
                url: `/ui-api/operator-account/offstreet-transactions/ticket/${context.ticketId}`,
            }),
            ticketId ? { ticketId } : null,
        );

    return (
        <>
            <OperatorOffstreetTransactionDetailRoutedModal
                render={controller => (
                    <OperatorAsyncLoadedSection
                        requestState={offstreetTransactionsState}
                        render={transaction => {
                            switch (
                                transaction.ticketOffstreetTransactionType
                            ) {
                                case TicketOffstreetTransactionType.TICKET:
                                    return (
                                        <RunningMonocardTicketDetailContent
                                            transaction={transaction}
                                            onClose={controller.close}
                                        />
                                    );
                                case TicketOffstreetTransactionType.TICKET_CONTRACT:
                                    return (
                                        <TerminatedTicketDetailContent
                                            transaction={transaction}
                                            onClose={controller.close}
                                            refetchTransaction={
                                                refetchOffstreetTransaction
                                            }
                                            onDirtyStateChange={
                                                controller.setShouldConfirmBeforeLeave
                                            }
                                        />
                                    );
                            }
                        }}
                    />
                )}
            />
        </>
    );
}

function RunningMonocardTicketDetailContent({
    transaction,
    onClose,
}: {
    transaction: MonocardTicketOffstreetTransactionDetails;
    onClose: () => void;
}) {
    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Ticket"
                            fr="Ticket"
                            it="Ticket"
                            en="Ticket"
                        />
                    }
                    editMode={false}
                    buttons={
                        <HeaderActionCloseButtons
                            onClose={onClose}
                            dropdownItems={[
                                {
                                    key: 'reimburse',
                                    label: (
                                        <OffstreetTransactionActionText
                                            action={
                                                OffstreetTransactionActions.REIMBURSE_CUSTOMER
                                            }
                                        />
                                    ),
                                    onClickCallback: () => {},
                                    disabled: true,
                                    tooltip: (
                                        <OffstreetTransactionRefundBlocked
                                            refundBlockedExplanation={
                                                transaction.refundBlockedExplanation
                                            }
                                        />
                                    ),
                                },
                                {
                                    key: 'download-receipt',
                                    label: (
                                        <OffstreetTransactionActionText
                                            action={
                                                OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                            }
                                        />
                                    ),
                                    onClickCallback: () => {},
                                    disabled: true,
                                    tooltip: <NoReceiptForRunningContract />,
                                },
                            ]}
                        />
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <OffstreetTransactionOpen
                            isPresent={true}
                            hasAntipassback={false}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                <BarrierGateIdentificationEnityValue
                                    barrierGateIdentificationEntity={
                                        transaction.barrierGateIdentificationEntity
                                    }
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Einfahrt"
                                    fr="Entrée"
                                    it="Entrata"
                                    en="Entry"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.entryInfo}
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ausfahrt"
                                    fr="Sortie"
                                    it="Uscita"
                                    en="Exit"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            }
                            value={transaction.zoneName}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '4px',
                                        alignItems: 'center',
                                    }}
                                >
                                    <PendingTicketPriceDisplay
                                        state={transaction.ticketState}
                                        unapprovedAt={transaction.unapprovedAt}
                                    />
                                </Box>
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={EmDash}
                        />
                        <OffstreetTransactionPayStationPayments
                            paystationPayments={null}
                        />
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Erstellt am"
                                        fr="Créé le"
                                        it="Creato il"
                                        en="Created at"
                                    />
                                }
                            >
                                {EmDash}
                            </BlockLabel>
                            <BlockLabel label="ID">{EmDash}</BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function TerminatedTicketDetailContent({
    transaction,
    onClose,
    refetchTransaction,
    onDirtyStateChange,
}: {
    transaction: TicketContractOffstreetTransactionDetails;
    onClose: () => void;
    refetchTransaction: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
}) {
    const localize = useLocalize();
    const { operatorExternalId } = useOperatorContext();

    const [openRefundResponseBanner, setOpenRefundResponseBanner] =
        useState(false);
    const [refundInputVisible, setRefundInputVisible] = useState(false);
    const [refundInputValues, setRefundInputValues] =
        useState<TransactionRefundInputValues>({
            amount: '',
            remark: '',
        });
    const [refundTransactionState, refundTransaction] = useServerWrite<
        TransactionRefundPayload,
        null
    >(({ contractExternalId }) => ({
        url: `/ui-api/operator-account/park-transaction/${contractExternalId}/refund`,
    }));

    function onRefundTransactionStateChange() {
        setOpenRefundResponseBanner(true);
        setRefundInputValues({
            amount: '',
            remark: '',
        });
        setRefundInputVisible(false);
    }

    useServerSuccessEffect(refundTransactionState, () => {
        refetchTransaction();
        onRefundTransactionStateChange();
    });

    useServerErrorEffect(
        refundTransactionState,
        onRefundTransactionStateChange,
    );

    useEffect(() => {
        onDirtyStateChange(refundInputVisible);
    }, [onDirtyStateChange, refundInputVisible]);

    const amountRappen = Math.round(parseChf(refundInputValues.amount) * 100);
    const isRefundable = isInputAmountRefundable(
        Math.round(transaction.priceChf * 100),
        amountRappen,
    );

    const refundAmountChf = transaction.refund
        ? transaction.refund.refundAmountRappen / 100
        : null;
    const netPayedPriceChf = getNetPayedPriceString(
        transaction.priceChf,
        refundAmountChf,
    );

    const hasNoReceipt =
        transaction.paystationPayments.length === 0 &&
        transaction.paymentChannel === PaymentChannel.PAY_STATION;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Ticket"
                            fr="Ticket"
                            it="Ticket"
                            en="Ticket"
                        />
                    }
                    editMode={refundInputVisible}
                    buttons={
                        !refundInputVisible ? (
                            <HeaderActionCloseButtons
                                onClose={onClose}
                                dropdownItems={[
                                    ...(transaction.showGuestPermitGeneration
                                        ? [
                                              {
                                                  key: 'generate-guest-permit',
                                                  label: (
                                                      <OffstreetTransactionActionText
                                                          action={
                                                              OffstreetTransactionActions.GENERATE_GUEST_PERMIT
                                                          }
                                                      />
                                                  ),
                                                  onClickCallback: () => {},
                                                  disabled: true,
                                                  tooltip: (
                                                      <Localized
                                                          de="Eine Gastkarte kann nur für laufende Parkvorgänge generiert werden"
                                                          fr="Une carte d'hôte ne peut être générée que pour les transactions de stationnement en cours"
                                                          it="Una carta ospite può essere generata solo per transazioni di parcheggio in corso"
                                                          en="A guest card can only be generated for ongoing parking transactions"
                                                      />
                                                  ),
                                              },
                                          ]
                                        : []),
                                    ...(transaction.isCloudConnector
                                        ? [
                                              {
                                                  key: 'recode',
                                                  label: (
                                                      <OffstreetTransactionActionText
                                                          action={
                                                              OffstreetTransactionActions.RECODE
                                                          }
                                                      />
                                                  ),
                                                  onClickCallback: () => {},
                                                  disabled: true,
                                                  tooltip: (
                                                      <RecodeBlockedForCompleteTransactions />
                                                  ),
                                              },
                                          ]
                                        : []),
                                    {
                                        key: 'reimburse',
                                        label: (
                                            <OffstreetTransactionActionText
                                                action={
                                                    OffstreetTransactionActions.REIMBURSE_CUSTOMER
                                                }
                                            />
                                        ),
                                        onClickCallback: () =>
                                            setRefundInputVisible(true),
                                        disabled:
                                            transaction.refundBlockedExplanation !==
                                            null,
                                        tooltip:
                                            transaction.refundBlockedExplanation ? (
                                                <OffstreetTransactionRefundBlocked
                                                    refundBlockedExplanation={
                                                        transaction.refundBlockedExplanation
                                                    }
                                                />
                                            ) : undefined,
                                    },
                                    {
                                        key: 'download-receipt',
                                        label: (
                                            <OffstreetTransactionActionText
                                                action={
                                                    OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                                }
                                            />
                                        ),
                                        onClickCallback: () => {
                                            openNewTab(
                                                urlWithOperatorExternalId(
                                                    `/ui-api/operator-account/park-transaction/${transaction.contractExternalId}/receipt`,
                                                    operatorExternalId,
                                                ),
                                            );
                                        },
                                        disabled: hasNoReceipt,
                                        tooltip: hasNoReceipt ? (
                                            <NoReceiptForContractWithoutPayments />
                                        ) : undefined,
                                    },
                                ]}
                            />
                        ) : (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    setRefundInputValues({
                                        amount: '',
                                        remark: '',
                                    });
                                    setRefundInputVisible(false);
                                }}
                                onSave={() => {
                                    if (isRefundable) {
                                        refundTransaction({
                                            contractExternalId:
                                                transaction.contractExternalId,
                                            amountRappen: amountRappen,
                                            remark: refundInputValues.remark,
                                        });
                                    }
                                }}
                                saveDisabled={!isRefundable}
                                loading={
                                    refundTransactionState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                        )
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <OffstreetTransactionOpen
                            isPresent={false}
                            hasAntipassback={false}
                            displayLpn={transaction.displayLpn}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                <BarrierGateIdentificationEnityValue
                                    barrierGateIdentificationEntity={
                                        transaction.barrierGateIdentificationEntity
                                    }
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Einfahrt"
                                    fr="Entrée"
                                    it="Entrata"
                                    en="Entry"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.entryInfo}
                                />
                            }
                        />
                        <OffstreetTransactionTransit
                            transitsInfo={transaction.transitsInfo}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ausfahrt"
                                    fr="Sortie"
                                    it="Uscita"
                                    en="Exit"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.exitInfo}
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={transaction.duration}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            }
                            value={transaction.zoneName}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <TransactionRefundResponseBanner
                            open={openRefundResponseBanner}
                            onClose={() => setOpenRefundResponseBanner(false)}
                            success={
                                refundTransactionState.status ===
                                RequestStatus.SUCCESS
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={netPayedPriceChf}
                        />
                        {refundInputVisible && (
                            <TransactionRefundInputFields
                                price={transaction.priceChf}
                                refundInputValues={refundInputValues}
                                setRefundInputValues={setRefundInputValues}
                            />
                        )}
                        {transaction.contractRecodes.length > 0 && (
                            <Stack
                                direction={'row'}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Rabatt"
                                                fr="Rabais"
                                                it="Sconto"
                                                en="Rebate"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.contractRecodes
                                                    .map(
                                                        contractRecode =>
                                                            contractRecode.recodeDiscount,
                                                    )
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Bedingung"
                                                fr="Condition"
                                                it="Condizione"
                                                en="Condition"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.contractRecodes
                                                    .map(contractRecode =>
                                                        localize(
                                                            contractRecode.recodeConditionDescription,
                                                        ),
                                                    )
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Stack>
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        <OffstreetTransactionPayStationPayments
                            paystationPayments={transaction.paystationPayments}
                        />
                        {transaction.refund && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Rückzahlung"
                                        fr="Remboursement"
                                        it="Rimborso"
                                        en="Refund"
                                    />
                                }
                                value={
                                    <>
                                        {`CHF ${(transaction.refund.refundAmountRappen / 100).toFixed(2)}`}
                                        <br />
                                        {Formatter.dayMonthYearHourMinute(
                                            DateTime.fromISO(
                                                transaction.refund.refundedAt,
                                            ),
                                        )}
                                        <br />
                                        {transaction.refund.remark}
                                        <br />
                                        {transaction.refund.refundedBy}
                                    </>
                                }
                            />
                        )}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Erstellt am"
                                        fr="Créé le"
                                        it="Creato il"
                                        en="Created at"
                                    />
                                }
                            >
                                {Formatter.dayMonthYearHourMinute(
                                    DateTime.fromISO(transaction.createdAt),
                                )}
                            </BlockLabel>
                            <BlockLabel label="ID">
                                {transaction.contractExternalId}
                            </BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

export function TranslationForTicketState({ state }: { state: TicketState }) {
    switch (state) {
        case TicketState.BOOKED:
            return <Localized de="Bezahlt" fr="Payé" it="Pagato" en="Paid" />;
        case TicketState.APPROVED:
            return (
                <Localized
                    de="Freigegeben"
                    fr="Approuvé"
                    it="Approvato"
                    en="Approved"
                />
            );
        default:
            return (
                <Localized
                    de="Zahlungsfreigabe fehlt"
                    fr="Approbation du paiement manquante"
                    it="Approvazione pagamento mancante"
                    en="Payment release missing"
                />
            );
    }
}

export function PendingTicketPriceDisplay({
    state,
    unapprovedAt,
}: {
    state: TicketState;
    unapprovedAt: string | null;
}): JSX.Element {
    const unapprovedAtFormatted = unapprovedAt
        ? Formatter.dayMonthYearHourMinute(DateTime.fromISO(unapprovedAt))
        : '';
    switch (state) {
        case TicketState.APPROVED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de={`Ablauf: ${unapprovedAtFormatted}`}
                            fr={`expiration: ${unapprovedAtFormatted}`}
                            it={`scadenza: ${unapprovedAtFormatted}`}
                            en={`expiry: ${unapprovedAtFormatted}`}
                        />
                    }
                >
                    <Box
                        sx={{
                            display: 'flex',
                            aligneItems: 'center',
                        }}
                    >
                        <PendingRounded color={'success'} />
                        <Box sx={{ width: '4px' }} />
                        <TranslationForTicketState state={state} />
                    </Box>
                </Tooltip>
            );
        case TicketState.ALIAS_PENDING:
            return (
                <>
                    <ErrorRounded color={'warning'} />
                    <TranslationForTicketState state={state} />
                </>
            );
        default:
            return <>{EmDash}</>;
    }
}

export function OffstreetTransactionPayStationPayments({
    paystationPayments,
}: {
    paystationPayments: PaystationPaymentData[] | null;
}) {
    return (
        <ReadOnlyTextField
            label={
                paystationPayments && paystationPayments.length < 2 ? (
                    <Localized
                        de="Kassenzahlung"
                        fr="Paiement à la caisse"
                        it="Pagamento alla cassa"
                        en="Pay station payment"
                    />
                ) : (
                    <Localized
                        de="Kassenzahlungen"
                        fr="Paiements à la caisse"
                        it="Pagamenti alla cassa"
                        en="Pay station payments"
                    />
                )
            }
            value={
                paystationPayments && paystationPayments.length !== 0
                    ? paystationPayments.map(payment => (
                          <Box key={payment.paidAt}>
                              {`CHF ${(payment.netPaidAmountRappen / 100).toFixed(2)} (${Formatter.hourMinute(
                                  DateTime.fromISO(payment.paidAt),
                              )})`}
                          </Box>
                      ))
                    : EmDash
            }
        />
    );
}

function RecodeBlockedForCompleteTransactions() {
    return (
        <Localized
            de="Ein Rabatt kann nur auf laufende Transaktionen angewendet werden"
            fr="Un rabais ne peut être appliquée que sur les transactions en cours"
            it="Un ribasso può essere applicato solo alle transazioni in corso"
            en="Discount can only be applied to ongoing parking transactions"
        />
    );
}
